import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';

interface StationEntry {
  id: string;
  name: string;
  formId: string;
}

const STATION_SPAWN_PREFIX = 'station-spawn:';
const STATION_SPAWN_CLOSE = 'station-spawn-close';

// No CLICK_GRACE_MS guard here (unlike ProfessionsMenu/TargetMenu) - this
// menu never opens as a side effect of another click landing under it; it
// only opens from the server pushing "showStationSpawner" after an admin
// deliberately typed "/spawntable" in chat, so there's no stray-click window
// to guard against.
const StationSpawner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [stations, setStations] = useState<StationEntry[]>([]);
  const [query, setQuery] = useState('');
  const [lastPlaced, setLastPlaced] = useState<string | null>(null);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { stations?: StationEntry[] };
      setStations(detail?.stations || []);
      setQuery('');
      setLastPlaced(null);
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showStationSpawner', showHandler);
    window.addEventListener('skymp5-client:hideStationSpawner', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showStationSpawner', showHandler);
      window.removeEventListener('skymp5-client:hideStationSpawner', hideHandler);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stations;
    return stations.filter((s) => s.name.toLowerCase().includes(q));
  }, [stations, query]);

  const place = (station: StationEntry) => {
    setLastPlaced(station.name);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(STATION_SPAWN_PREFIX + station.id);
    }
  };

  const close = () => {
    setVisible(false);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(STATION_SPAWN_CLOSE);
    }
  };

  if (!visible) return null;

  return (
    <div className="station-spawner-overlay">
      <div className="station-spawner-shell">
        <header className="station-spawner-header">
          <div className="station-spawner-header__brand">Altherys RP - Admin</div>
          <h1 className="station-spawner-header__title">Placer une station d'artisanat</h1>
          <div className="station-spawner-header__tagline">
            Apparaît devant toi, à ta position exacte.
          </div>
        </header>

        <div className="station-spawner-search">
          <input
            type="text"
            autoFocus
            placeholder="Rechercher..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <ul className="station-spawner-list">
          {filtered.map((s) => (
            <li key={s.id}>
              <button type="button" className="station-spawner-item" onClick={() => place(s)}>
                <span className="station-spawner-item__name">{s.name}</span>
                <span className="station-spawner-item__id">{s.formId}</span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="station-spawner-empty">Aucune station trouvée.</li>
          )}
        </ul>

        <footer className="station-spawner-footer">
          <div className="station-spawner-footer__hint"><kbd>Échap</kbd> Fermer</div>
          <div className="station-spawner-footer__status">
            {lastPlaced ? `${lastPlaced} posé(e) - tu peux en placer une autre.` : ' '}
          </div>
        </footer>

        <button type="button" className="station-spawner-close" onClick={close} aria-label="Fermer">×</button>
      </div>
    </div>
  );
};

export default StationSpawner;
